import { ArrowLeft, Building2, ListPlus } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { RequireCapability } from "@/components/RequireCapability";
import { RequireSubscription } from "@/components/RequireSubscription";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/auth-context";
import { getOnBehalfOf } from "@/lib/api/on-behalf";
import { cn } from "@/lib/utils/cn";

import { useProfile } from "./api";
import { ErrorBlock, LoadingBlock } from "./components/QueryState";

export { EnterpriseProfilePage } from "./EnterpriseProfilePage";
import { missingRequired } from "./profile-utils";
import { AnsweringStep } from "./steps/AnsweringStep";
import { ConsentGate } from "./steps/ConsentGate";
import { CreateProfileGate, MissingFieldsGate } from "./steps/ProfileGate";
import { IssuesStep } from "./steps/IssuesStep";
import { ProfileEditor } from "./steps/ProfileEditor";
import { ResultsStep } from "./steps/ResultsStep";
import type { FlushFn } from "./types";

/**
 * Self-diagnostic journey. The enterprise profile is a *gate*, not a step: we
 * only interrupt for it when it's missing or incomplete. Once past the gate the
 * journey is simply Answer ↔ Results, with the profile and issue selection as
 * quiet side-doors — never blocking the path. State is held locally; every
 * editable surface autosaves.
 */
export function QuestionnairePage() {
  return (
    <RequireCapability required={["authenticated"]}>
      <RequireSubscription>
        <QuestionnaireExperience />
      </RequireSubscription>
    </RequireCapability>
  );
}

function QuestionnaireExperience() {
  const { user } = useAuth();
  // Partner mode targets the member's profile (on_behalf_of); else the user's own.
  const [profileId, setProfileId] = useState<string | null>(
    getOnBehalfOf() ?? user?.enterprise_profile_id ?? null,
  );

  // No profile yet (brand-new self user): create it once before the diagnostic.
  if (!profileId) {
    return (
      <Shell>
        <CreateProfileGate onCreated={setProfileId} />
      </Shell>
    );
  }
  return <ProfileReadyRouter profileId={profileId} />;
}

/** Ensures the profile's required fields are present before the diagnostic. */
function ProfileReadyRouter({ profileId }: { profileId: string }) {
  const { user } = useAuth();
  // Reading someone else's diagnostic skips the gate and the profile fetch
  // behind it. The gate exists to make a *member* finish their own profile
  // before answering; putting a partner or an administrator through it would
  // have them completing an account that is not theirs — and the profile
  // itself is not theirs to read here either, which is what closed the
  // enterprise-profile retrieve to anyone but an administrator.
  const consulting = getOnBehalfOf() != null;
  const profile = useProfile(consulting ? null : profileId);
  const [gatePassed, setGatePassed] = useState(false);
  const [consentPassed, setConsentPassed] = useState(false);
  // Below the hooks, never above them: an early return that skips a `useState`
  // changes the hook count between renders the moment anything makes this
  // instance re-render with a different answer.
  if (consulting) return <Diagnostic profileId={profileId} />;

  if (profile.isLoading) {
    return (
      <Shell>
        <LoadingBlock />
      </Shell>
    );
  }
  if (profile.error || !profile.data) {
    return (
      <Shell>
        <ErrorBlock error={profile.error} />
      </Shell>
    );
  }

  const missing = missingRequired(profile.data);
  if (missing.length > 0 && !gatePassed) {
    return (
      <Shell>
        <MissingFieldsGate
          profileId={profileId}
          missing={missing}
          onReady={() => setGatePassed(true)}
        />
      </Shell>
    );
  }

  // Mandatory consent step for group members who haven't decided yet (K-3),
  // mirroring the old app's member_consent step after identification. Skipped
  // in partner on-behalf mode — the consent belongs to the member, not the
  // partner answering for them.
  const needsConsent =
    !getOnBehalfOf() && (user?.permissions.needs_partner_consent ?? false);
  if (needsConsent && !consentPassed) {
    return (
      <Shell>
        <ConsentGate onDone={() => setConsentPassed(true)} />
      </Shell>
    );
  }

  return <Diagnostic profileId={profileId} />;
}

type View = "answer" | "results" | "profile" | "issues";

function Diagnostic({ profileId }: { profileId: string }) {
  const { t } = useTranslation();
  // Honour a `?view=results|issues` deep-link (dashboard action-hub shortcuts);
  // default to the answering surface. `?mode=focus|browse` picks the answering
  // surface's layout (the dashboard "continue" CTA opens focus mode).
  const search = useSearch({ strict: false }) as {
    view?: "results" | "issues";
    mode?: "browse" | "focus";
    from?: "communications";
  };
  const [view, setView] = useState<View>(search.view ?? "answer");
  const routerNavigate = useNavigate();
  // Where the "add issues" surface was opened from, so its Back returns there:
  // the communication issue step or the dashboard when deep-linked via
  // ?view=issues, otherwise the questionnaire tab it was opened from.
  const [issuesOrigin, setIssuesOrigin] = useState<IssuesOrigin>(
    search.view !== "issues"
      ? "answer"
      : search.from === "communications"
        ? "communications"
        : "dashboard",
  );

  // The active surface registers its autosave flush so navigation persists a
  // pending debounced edit before leaving.
  const flushRef = useRef<FlushFn | null>(null);
  const registerFlush = useCallback((fn: FlushFn | null) => {
    flushRef.current = fn;
  }, []);
  const navigate = useCallback(async (next: View) => {
    await flushRef.current?.();
    setView(next);
  }, []);
  // In-app entry to the "add issues" surface — mark the tab it was opened
  // from so Back returns to it.
  const openIssues = useCallback(
    async (from: "answer" | "results") => {
      setIssuesOrigin(from);
      await navigate("issues");
    },
    [navigate],
  );
  const leaveIssues = useCallback(() => {
    if (issuesOrigin === "dashboard") void routerNavigate({ to: "/dashboard" });
    else if (issuesOrigin === "communications")
      void routerNavigate({ to: "/communications", search: { step: "issues" } });
    else void navigate(issuesOrigin);
  }, [issuesOrigin, navigate, routerNavigate]);

  const onMainTabs = view === "answer" || view === "results";

  return (
    <Shell>
      {onMainTabs && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div
            role="tablist"
            aria-label={t("questionnaire.nav.label")}
            className="inline-flex gap-1 rounded-lg border bg-secondary p-1"
          >
            <TabButton
              active={view === "answer"}
              onClick={() => void navigate("answer")}
            >
              {t("questionnaire.nav.answer")}
            </TabButton>
            <TabButton
              active={view === "results"}
              onClick={() => void navigate("results")}
            >
              {t("questionnaire.nav.results")}
            </TabButton>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void openIssues(view === "results" ? "results" : "answer")}
            >
              <ListPlus aria-hidden />
              {t("questionnaire.nav.addIssues")}
            </Button>
            {getOnBehalfOf() == null && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void navigate("profile")}
              >
                <Building2 aria-hidden />
                {t("questionnaire.nav.profile")}
              </Button>
            )}
          </div>
        </div>
      )}

      {view === "answer" && (
        <AnsweringStep
          onContinue={() => void navigate("results")}
          registerFlush={registerFlush}
          initialMode={search.mode}
        />
      )}
      {view === "results" && (
        <ResultsStep
          onBack={() => void navigate("answer")}
          onExploreIssues={() => void openIssues("results")}
        />
      )}
      {view === "profile" && (
        <ProfileEditor profileId={profileId} onBack={() => void navigate("answer")} />
      )}
      {view === "issues" && (
        <div className="space-y-4">
          <BackLink onClick={leaveIssues}>{t(BACK_LABEL[issuesOrigin])}</BackLink>
          <IssuesStep onContinue={() => void navigate("answer")} />
        </div>
      )}
    </Shell>
  );
}

/** Where the "add issues" surface was opened from. */
type IssuesOrigin = "answer" | "results" | "dashboard" | "communications";

/** Its Back link names the place it returns to. */
const BACK_LABEL: Record<IssuesOrigin, string> = {
  answer: "questionnaire.nav.back",
  results: "questionnaire.nav.backToResults",
  dashboard: "questionnaire.nav.backToDashboard",
  communications: "questionnaire.nav.backToCommunications",
};

/** Page chrome shared by every phase of the journey. */
function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t("questionnaire.title")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("questionnaire.subtitle")}</p>
      </div>
      {children}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded-md px-4 py-1.5 text-sm font-semibold transition-colors",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function BackLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {children}
    </button>
  );
}
